import { AvailabilityType, Booking } from '../../../@generated/schemas';
import {
  DatesSetArg,
  EventClickArg,
  EventInput
} from '@fullcalendar/core';
import { useEffect, useState } from 'react';
import { useRecordContext, useRefresh } from 'react-admin';

import { AvailabilityDetailModal } from './availabilityDetailModal';
import { BookingDetailModal } from './bookingDetailModal';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import dayjs from 'dayjs';
import i18next from 'i18next';
import interactionPlugin from '@fullcalendar/interaction';
// import { makeStyles } from '@mui/material';
import timeGridPlugin from '@fullcalendar/timegrid';
import { useTranslate } from '../../../locales';
import { useVehicleAvailabilityLazyQuery } from '../../../@generated/hooks';

const defaultNewEventValues = {
  availabilityId: undefined,
  comment: '',
  sharedVehicleGroups: [],
  startDateTime: null,
  endDateTime: null,
  expiredAt: null,
  isAvailable: true,
  type: AvailabilityType.Single,
};

export const VehicleAvailability = () => {
  const refresh = useRefresh();
  const record = useRecordContext();
  const [showEventForm, setShowEventForm] = useState<boolean>(false);
  const [filter, setFilter] = useState({
    startDate: dayjs().startOf('month').format(),
    endDate: dayjs().startOf('month').add(47, 'day').format(),
  });
  const translate = useTranslate();
  const [newEvent, setNewEvent] = useState({
    ...defaultNewEventValues,
    vehicleId: record?.id,
  });
  const [bookingDetail, setBookingDetail] = useState<Booking>(undefined);
  const [fetchAvailability, { data }] = useVehicleAvailabilityLazyQuery({
    fetchPolicy: 'no-cache',
  });

  const refetch = (filter) =>
    record?.id &&
    fetchAvailability({
      variables: {
        vehicleId: record.id as string,
        startDate: new Date(filter.startDate),
        endDate: new Date(filter.endDate),
      },
    });

  useEffect(() => {
    refetch(filter);
  }, []);

  const handleDatesSet = (dateInfo: DatesSetArg): void => {
    const newFilter = {
      startDate: dateInfo.startStr,
      endDate: dateInfo.endStr,
    };
    setFilter(newFilter);
    refetch(newFilter);
  };

  const handleDateSelect = (event: any): void => {
    if (dayjs(event.start).isBefore(dayjs().subtract(1, 'days'))) return;

    setNewEvent({
      ...newEvent,
      startDateTime: event.start,
      endDateTime: event.end,
    });
    const calendarApi = event.view.calendar;
    calendarApi.unselect();

    setShowEventForm(true);
  };

  const handleEventClick = (event: EventClickArg & any): void => {
    const { bookingHash } = event.event._def.extendedProps;
    const eventStart = event.event.start;

    const rect = event.el.getBoundingClientRect();
    const clickX = event.jsEvent.clientX - rect.left;

    const days =
      Math.round((event.event.end - event.event.start) / (1000 * 60 * 60 * 24)) || 1;

    const dayWidth = rect.width / days;
    const dayIndex = Math.floor(clickX / dayWidth);

    const clickedDate = new Date(eventStart);
    clickedDate.setDate(eventStart.getDate() + dayIndex);

    if (bookingHash) {
      setBookingDetail({
        ...(event.event._def.extendedProps as any),
        clickedDate,
        id: event.event.id,
      });
    } else {
      setNewEvent({
        ...newEvent,
        ...event.event._def.extendedProps,
        clickedDate,
      });
      setShowEventForm(true);
    }

    const calendarApi = event.view.calendar;
    calendarApi.unselect();
  };

  const handleCloseModal = () => {
    setBookingDetail(undefined);
    setShowEventForm(false);
    setNewEvent({
      ...defaultNewEventValues,
      vehicleId: record?.id,
    });
    refresh();
  };

  const handleChangeNewEventProperty = (property: string, value: any) => {
    setNewEvent({
      ...newEvent,
      [property]: value,
    });
  };

  const getFormattedEvents = (): EventInput[] => {
    const availabilityEvents = (
      data?.vehicleAvailability?.availability || []
    ).map((event, key): EventInput => {
      const fixedZone = "Etc/GMT-1";
      return {
        id: key.toString(),
        ...event,
        sharedVehicleGroups: event.sharedVehicleGroupRelAvailability.map(
          (item) => item.sharedVehicleGroup?.id,
        ),
        start: dayjs(event.startDateTime).tz(fixedZone).set('seconds', 0).toISOString(),
        end: dayjs(event.endDateTime).tz(fixedZone).set('seconds', 0).toISOString(),
        backgroundColor: event.isAvailable ? 'blue' : 'red',
        color: event.isAvailable ? 'blue' : 'red',
        title: event.comment || '',
        editable: true,
      };
    });
    const bookingEvents = (data?.vehicleAvailability?.bookings || []).map(
      (booking): EventInput => ({
        id: booking.id,
        ...booking,
        start: dayjs(booking.effectiveStartDate || booking.startDate)
          .toISOString(),
        end: dayjs(booking.effectiveEndDate || booking.endDate)
          .toISOString(),
        backgroundColor: 'black',
        color: 'black',
        title: `Booking: ${booking.bookingHash}`,
        editable: true,
      }),
    );

    return [...availabilityEvents, ...bookingEvents];
  };

  const createNewEvent = () => {
    setNewEvent({
      ...newEvent,
      ...defaultNewEventValues,
    });

    setShowEventForm(true);
  };

  const handleEventDidMount = (info) => {
    if (info.view.type === 'dayGridMonth') {
      const dateCell = info.el.closest('.fc-daygrid-day');
      const date = dateCell?.getAttribute('data-date');

      if (date) {
        info.el.dataset.segmentDate = date;
      }
    }
  };

  return (
    <div>
      <div>
        <FullCalendar
          height="60vh"
          titleFormat={{
            year: 'numeric',
            month: 'long',
            day: 'numeric',
          }}
          views={{
            dayGridMonth: {
              dayHeaderFormat: {
                weekday: 'long',
              },
            },
            timeGridWeek: {
              dayHeaderFormat: {
                day: 'numeric',
                month: 'long',
              },
            },
            timeGridDay: {
              dayHeaderFormat: {
                weekday: 'long',
                day: 'numeric',
              },
            },
          }}
          eventTimeFormat={{
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
            meridiem: false,
            hour12: false,
          }}
          slotLabelFormat={{
            hour: '2-digit',
            minute: '2-digit',
            meridiem: false,
            hour12: false,
          }}
          firstDay={1}
          customButtons={{
            addEventButton: {
              text: translate('admin.addEvent'),
              click: () => createNewEvent(),
            },
          }}
          eventAllow={() => false}
          plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
          headerToolbar={{
            left: 'prev,next today',
            center: 'title',
            right: 'addEventButton dayGridMonth,timeGridWeek,timeGridDay',
          }}
          eventDidMount={handleEventDidMount}
          timeZone="local"
          locale={i18next.language}
          buttonText={{
            today: translate('admin.today'),
            month: translate('admin.month'),
            week: translate('admin.week'),
            day: translate('admin.day'),
          }}
          initialView="dayGridMonth"
          selectable
          nowIndicator
          dayMaxEvents
          navLinks
          datesSet={handleDatesSet}
          select={handleDateSelect}
          events={getFormattedEvents()}
          eventClick={handleEventClick}
        />
      </div>

      <AvailabilityDetailModal
        newEvent={newEvent}
        open={showEventForm}
        onClose={handleCloseModal}
        refetch={refetch}
        filter={filter}
        onChange={handleChangeNewEventProperty}
      />

      <BookingDetailModal
        bookingDetail={bookingDetail}
        onClose={handleCloseModal}
      />
    </div>
  );
};
